import UserEventHandler from '@sendbird/uikit-react/handlers/UserEventHandler';
import sbSelectors from '@sendbird/uikit-react/sendbirdSelectors';
import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setUnreadCount } from '../actions';

/**
 * Invisible component that syncs the Sendbird total unread message count
 * into the Redux store. Mount inside a SendBirdProvider + MessagingProvider
 * (which gates on connection). This ensures the nav badge stays in sync
 * even when the app header's UnreadCount component is not mounted (e.g. mobile).
 */
export function SyncUnreadToRedux() {
    const dispatch = useDispatch();
    const sbContext = useSendbirdStateContext();
    const sdk = sbSelectors.getSdk(sbContext);

    const handler = useMemo(
        () =>
            new UserEventHandler({
                onTotalUnreadMessageCountChanged: c => dispatch(setUnreadCount(c.groupChannelCount)),
            }),
        [dispatch],
    );

    useEffect(() => {
        // getSdk() can return a partial SDK object that lacks user-event methods;
        // guard to avoid "addUserEventHandler is not a function" (SMARTLY-KHN)
        if (!sdk?.addUserEventHandler) return undefined;
        let cancelled = false;

        const fetchCount = () => {
            if (cancelled) return;
            if (!sdk.groupChannel) {
                // groupChannel may not be initialized yet on cold start; retry shortly
                setTimeout(fetchCount, 200);
                return;
            }
            // Explicit `: number` because Sendbird SDK types don't fully resolve under bundler moduleResolution.
            // Remove annotation if @sendbird/chat fixes its exports map.
            sdk.groupChannel.getTotalUnreadMessageCount().then((c: number) => {
                if (!cancelled) dispatch(setUnreadCount(c));
            });
        };

        fetchCount();
        sdk.addUserEventHandler('redux_unread_sync', handler);
        return () => {
            cancelled = true;
            sdk.removeUserEventHandler('redux_unread_sync');
        };
    }, [sdk, handler, dispatch]);

    return null;
}
